import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { publicPostApi } from '../../api';
import Button from '../ui/Button';

const VerifyOtp = () => {
	const navigate = useNavigate();
	const params = useParams();
	const phoneNumber = params.phoneNumber ? decodeURIComponent(params.phoneNumber) : null;

	const [isLoading, setIsLoading] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [isError, setIsError] = useState(false);
	const [otp, setOtp] = useState('');
	const firstInput = useRef(null);
	const secondInput = useRef(null);
	const thirdInput = useRef(null);
	const fourthInput = useRef(null);
	const fifthInput = useRef(null);
	const sixthInput = useRef(null);
	const [inputValues, setInputValues] = useState(new Array(6).fill(''));
	const [isComplete, setIsComplete] = useState(false);
	const inputs = useRef(null);

	useEffect(() => {
		inputs.current = document.querySelectorAll('#otp > *[id]');
	}, [inputValues]);

	useEffect(() => {
		setOtp(inputValues.join(''));
		setIsComplete(inputValues.join('').length === 6);
	}, [inputValues]);

	const inputRefs = [
		firstInput,
		secondInput,
		thirdInput,
		fourthInput,
		fifthInput,
		sixthInput,
	];

	const handleInputChange = (event, index) => {
		let value = event.target.value;
		const pattern = /^\d*$/;
		if (!pattern.test(value)) value = '';
		const newInputValues = [...inputValues];
		newInputValues[index] = value;
		setInputValues(newInputValues);
		if (value !== '' && index < 5) {
			inputRefs[index + 1].current?.focus();
		}
	};

	const handleVerifyOtp = async (e) => {
		e.preventDefault();
		const code = inputValues.join('');
		if (code.length !== 6) {
			toast.error('OTP must be 6 digits');
			setIsError('OTP must be 6-digit number');
			setTimeout(() => setIsError(false), 2000);
			return;
		}
		setIsLoading(true);
		setIsError(false);

		// Password reset flow: validate-code → set-new-password
		publicPostApi('system/auth/password/validate-code/', {
				phone_number: phoneNumber,
				code,
			})
			.then((data) => {
				const msg = data?.msg ?? data?.message ?? 'Code verified.';
				toast.success(msg);
				setIsLoading(false);
				setIsSuccess(true);
				navigate('/set-new-password', {
					replace: true,
					state: { phone_number: phoneNumber, code },
				});
			})
			.catch((error) => {
				setIsLoading(false);
				const msg = error?.message ?? 'Something went wrong. Please try again.';
				toast.error(msg);
				setIsError(msg);
			});
	};

	const maskPhone = (p) => {
		if (!p || p.length < 6) return p;
		return p.slice(0, 4) + '***' + p.slice(-3);
	};

	const subtitle = phoneNumber
		? `Enter the 6-digit code sent to ${maskPhone(phoneNumber)}`
		: 'Enter the 6-digit verification code';

	return (
		<div className="overflow-hidden bg-white mx-auto mt-10 md:mt-20 md:mb-5 p-4 md:p-2 w-full md:max-w-[600px] shadow-xl rounded-lg">
			<div className="text-center mb-6">
				<h2 className="text-3xl font-bold text-black">Verify OTP</h2>
				<p className="text-sm md:text-base text-gray-500 mt-2 mb-8 sm:mb-10">
					{subtitle}
				</p>
			</div>
			<div className="max-w-sm mx-auto md:max-w-lg my-5">
				<form className="bg-gray-50 relative min-h-64 py-3 rounded text-center" onSubmit={handleVerifyOtp}>
					<div
						id="otp"
						className="flex flex-row justify-center text-center px-2 mt-5"
					>
						<input
							className="m-1 md:m-2 border h-10 w-10 text-center form-control rounded"
							type="text"
							inputMode="numeric"
							maxLength={1}
							ref={firstInput}
							value={inputValues[0]}
							onChange={(e) => handleInputChange(e, 0)}
							onKeyDown={(e) => { if (e.key === 'Backspace' && !inputValues[0]) firstInput.current?.focus(); }}
						/>
						<input
							className="m-1 md:m-2 border h-10 w-10 text-center form-control rounded"
							type="text"
							inputMode="numeric"
							maxLength={1}
							ref={secondInput}
							value={inputValues[1]}
							onChange={(e) => handleInputChange(e, 1)}
							onKeyDown={(e) => { if (e.key === 'Backspace' && !inputValues[1]) firstInput.current?.focus(); }}
						/>
						<input
							className="m-1 md:m-2 border h-10 w-10 text-center form-control rounded"
							type="text"
							inputMode="numeric"
							maxLength={1}
							ref={thirdInput}
							value={inputValues[2]}
							onChange={(e) => handleInputChange(e, 2)}
							onKeyDown={(e) => { if (e.key === 'Backspace' && !inputValues[2]) secondInput.current?.focus(); }}
						/>
						<input
							className="m-1 md:m-2 border h-10 w-10 text-center form-control rounded"
							type="text"
							inputMode="numeric"
							maxLength={1}
							ref={fourthInput}
							value={inputValues[3]}
							onChange={(e) => handleInputChange(e, 3)}
							onKeyDown={(e) => { if (e.key === 'Backspace' && !inputValues[3]) thirdInput.current?.focus(); }}
						/>
						<input
							className="m-1 md:m-2 border h-10 w-10 text-center form-control rounded"
							type="text"
							inputMode="numeric"
							maxLength={1}
							ref={fifthInput}
							value={inputValues[4]}
							onChange={(e) => handleInputChange(e, 4)}
							onKeyDown={(e) => { if (e.key === 'Backspace' && !inputValues[4]) fourthInput.current?.focus(); }}
						/>
						<input
							className="m-1 md:m-2 border h-10 w-10 text-center form-control rounded"
							type="text"
							inputMode="numeric"
							maxLength={1}
							ref={sixthInput}
							value={inputValues[5]}
							onChange={(e) => handleInputChange(e, 5)}
							onKeyDown={(e) => { if (e.key === 'Backspace' && !inputValues[5]) fifthInput.current?.focus(); }}
						/>
					</div>
					<div className="w-full mt-5">
						{isError && <div className="text-red-500 text-sm mb-2">{isError}</div>}
						{isSuccess && <div className="text-brand-700 text-sm mb-2">Verified</div>}
						<Button type="submit" size="lg" fullWidth disabled={isLoading} className="md:max-w-[200px] mx-auto my-1">
							{isLoading ? 'Verifying…' : 'Verify OTP'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default VerifyOtp;
